import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeCatalog, updateCatalog } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";
import moment from "moment";
import ImageDropzone from "components/ImageDropzone";
import { InputSelect } from "components/Form/InputSelect";
import { getRole } from "hooks";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GridWrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

interface Props {
  catalogId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  catalogName: "",
  pointsNeeded: "",
  expiryDate: "",
  catalogType: "",
  catalogProduct: "",
  pointTypeUsed: "",
};

const CatalogFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  catalogId,
}) => {
  const isAddMode = !catalogId;
  const dispatch = useDispatch();
  const userRole = getRole();
  const formikRef = useRef<FormikValues>() as any;

  const [catalogType, setCatalogType] = useState<ValueSchema | null>(null);
  const [pointTypeUsed, setPointTypeUsed] = useState<ValueSchema | null>(null);
  const [tenant, setTenant] = useState<ValueSchema | null>(null);
  const [filesCatalogImage, setFilesCatalogImage] = useState<any>([]);

  const [catalogTypeItems] = React.useState([
    { label: "Auto", value: "auto" },
    { label: "Manual", value: "manual" },
  ]);
  const [pointTypeUsedItems] = React.useState([
    { label: "XP Point", value: 0 },
    { label: "Reward Point", value: 1 },
    { label: "Push Reward", value: 2 },
  ]);
  const [tenantItems] = React.useState([
    // { label: "Ultra Milk", value: 2 },
    // { label: "BAT", value: 1 },
    { label: "Ancol Points", value: 3 },
  ]);

  const { appUrl } = useSelector((state: any) => state.globalReducer);
  const { catalogs, isFormLoading } = useSelector(
    (state: any) => state.catalogReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
    setCatalogType(null);
    setPointTypeUsed(null);
    setTenant(null);
    setFilesCatalogImage([]);
  };

  const handleSubmit = (values: any) => {
    const {
      catalogName,
      pointsNeeded,
      expiryDate,
      catalogType,
      catalogProduct,
      pointTypeUsed,
      tenantId,
    } = values;
    const catalogImage =
      typeof filesCatalogImage[0] === "object" ? filesCatalogImage[0] : null;

    isAddMode
      ? dispatch(
          storeCatalog(
            {
              catalogName,
              pointsNeeded,
              expiryDate,
              catalogType,
              catalogProduct,
              pointTypeUsed,
              catalogImage,
              tenantId,
            },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateCatalog(
            catalogId,
            {
              catalogName,
              pointsNeeded,
              expiryDate,
              catalogType,
              catalogProduct,
              pointTypeUsed,
              catalogImage,
              tenantId,
            },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const catalogDetail = catalogs.find(
        (catalog: any) => catalog.id === parseInt(catalogId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue(
          "catalogName",
          catalogDetail.catalog_name
        );
        formikRef.current.setFieldValue(
          "pointsNeeded",
          catalogDetail.points_needed
        );
        formikRef.current.setFieldValue(
          "expiryDate",
          moment(catalogDetail.expiry_date).format("YYYY-MM-DD")
        );
        formikRef.current.setFieldValue(
          "catalogType",
          catalogDetail.catalog_type
        );
        formikRef.current.setFieldValue(
          "catalogProduct",
          catalogDetail.catalog_product
        );
        formikRef.current.setFieldValue(
          "pointTypeUsed",
          catalogDetail.point_type_used
        );
        formikRef.current.setFieldValue("tenantId", catalogDetail.tenant_id);

        catalogTypeItems.map((item) =>
          item.value === catalogDetail.catalog_type
            ? setCatalogType(item)
            : null
        );
        pointTypeUsedItems.map((item) =>
          item.value.toString() === catalogDetail.point_type_used
            ? setPointTypeUsed(item)
            : null
        );
        tenantItems.map((item) =>
          item.value.toString() === catalogDetail.tenant_id
            ? setTenant(item)
            : null
        );
        setFilesCatalogImage([`${appUrl}/storage/${catalogDetail.img_url}`]);
      }
    }
  }, [isAddMode, catalogs, catalogId, appUrl]);

  const validationSchema = yup.object({
    catalogName: yup.string().required("Catalog Name is required field"),
    pointsNeeded: yup.number().required("Points Needed is required field"),
    expiryDate: yup.date().required("Expiry Date is required field"),
    catalogType: yup.string().required("Catalog Type is required field"),
    catalogProduct: yup.string().required("Catalog Product is required field"),
    pointTypeUsed: yup.number().required("Point Type Used is required field"),
    tenantId:
      userRole === "superadmin"
        ? yup.string().required("Tenant is required field")
        : yup.string(),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add Catalog" : "Update Catalog"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="catalogName">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="catalogName">
                          Catalog Name
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <GridWrapper>
                    <Field name="pointsNeeded">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="pointsNeeded">
                            Points Needed
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="number"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="expiryDate">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="expiryDate">
                            Expiry Date
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="date"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>
                  <Field name="catalogProduct">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="catalogProduct">
                          Catalog Product
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <GridWrapper>
                    <Field name="catalogType">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="catalogType">
                            Catalog Type
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={catalogType}
                              width={"100%"}
                              placeholder="Select Catalog Type"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setCatalogType(selectedItem);
                                  setFieldValue(
                                    "catalogType",
                                    selectedItem.value
                                  );
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={catalogTypeItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="pointTypeUsed">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="pointTypeUsed">
                            Point Type Used
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={pointTypeUsed}
                              width={"100%"}
                              placeholder="Select Point Type Used"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setPointTypeUsed(selectedItem);
                                  setFieldValue(
                                    "pointTypeUsed",
                                    selectedItem.value
                                  );
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={pointTypeUsedItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>{" "}
                  {userRole === "superadmin" ? (
                    <Field name="tenantId">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="tenantId">Tenant</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={tenant}
                              width={"100%"}
                              placeholder="Select Tenant"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setTenant(selectedItem);
                                  setFieldValue("tenantId", selectedItem.value);
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={tenantItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : (
                    <></>
                  )}
                  <Stack spacing="xs">
                    <FormLabel>Image</FormLabel>
                    <ImageDropzone
                      files={filesCatalogImage}
                      setFiles={setFilesCatalogImage}
                    />
                  </Stack>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CatalogFormModal;
